import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";

// i18n
import { useTranslation } from "react-i18next";

// Active Storage
import { DirectUpload } from "@rails/activestorage";

// Icons
import { RiRobot3Line } from "react-icons/ri";

// Types
import { APIChatMessage, ChatMessage } from "../../types";

// Components
import ChatInput from "../ChatInput";
import MessageRow from "./MessageRow";
import AssistantMessage from "./AssistantMessage";
import Waiting from "./Waiting";

// Utils
// import { chatUrl } from '../../utils/urls';
import actionCableConsumer from '../../utils/actionCable';

// Styles
import styles from "./style.module.scss";

// TODO: Daniel: Add a prop to pass the channel name from the backend when we have it
// TODO: Daniel: Add a prop to pass the starting messages or load them from the backend when we have it
interface ChatProps {
  startingMessages?: APIChatMessage[];
  passedFiles?: File[];
  onRenderEmptyChat?: () => React.ReactNode;
  onTopOfChat?: () => React.ReactNode;
}

const defaultEmptyChat = () => null;
const defaultTopOfChat = () => null;

const Chat: React.FC<ChatProps> = ({
  startingMessages = [],
  passedFiles = [],
  onRenderEmptyChat = defaultEmptyChat,
  onTopOfChat = defaultTopOfChat
}) => {
  const { t } = useTranslation();

  // States
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isWaiting, setIsWaiting] = useState<boolean>(false);
  const [messages, setMessages] = useState<APIChatMessage[]>([]);

  // Ref for auto-scroll
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const subscription = useRef<ActionCable.Subscription | null>(null);

  // Methods
  const sendMessage = useCallback(async (message: APIChatMessage) => {
    setIsSending(true);
    try {

      // TODO: Remove this
      await new Promise(resolve => setTimeout(resolve, 1000));
      setMessages(prevMessages => [...prevMessages, { ...message, id: crypto.randomUUID() }]);
      // WAITING FOR ASSISTANT TO RESPOND
      setIsWaiting(true);

      // TODO: Uncomment this when we have the API endpoint
      //   const formData = new FormData();
      //   if (message.text) formData.append('message', message.text);
      //   if (message.file) formData.append('file', message.file.signed_id);

      //   if (formData.values.length === 0) {
      //     throw new Error('No message or file to send');
      //   }

      //   const options = {
      //     method: 'POST',
      //     body: formData,
      //   };
      //   const response = await fetch(chatUrl(), options);
      //   if (response.ok) {
      //     setMessages(prevMessages => [...prevMessages, message]);
      //     setIsWaiting(true);
      //   }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSending(false);
    }
  }, []);

  const messageReceived = useCallback((data: APIChatMessage) => {
    setMessages(prevMessages => [...prevMessages, data]);
  }, []);

  // Auto-scroll to bottom when messages or isWaiting changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isWaiting]);

  useEffect(() => {
    // TODO: Daniel: Use the channel name from the backend when we have it
    subscription.current = actionCableConsumer.subscriptions.create({ channel: 'ChatChannel' }, {
      received: messageReceived,
      connected: () => {
      },
      disconnected: () => {
      }
    });

    return () => {
      subscription.current?.unsubscribe();
    };
  }, []);

  const handleSend = useCallback(async (message: ChatMessage) => {
    if (message.files && message.files.length > 0) {
      for (const file of message.files) {
        await sendMessage({ role: 'user', file: file });
      }
    }
    if (message.text) {
      await sendMessage({ role: 'user', ...message });
    }
  }, [sendMessage]);

  const renderEmptyChat = () => {
    if (messages?.length === 0) {
      return onRenderEmptyChat?.() || defaultEmptyChat();
    }
    return null;
  };

  const mainContainerClasses = "flex flex-col flex-1 gap-2";
  return (
    <div className={mainContainerClasses}>
      <div className={styles.messagesContainer} style={{ maxHeight: '100%', overflowY: 'auto' }}>
        {onTopOfChat?.()}
        {renderEmptyChat()}
        <AssistantMessage />
        {messages.map((message) => <MessageRow key={message.id} message={message} />)}
        {/* This div is used as the scroll target */}
        <div ref={messagesEndRef} />
      </div>

      {isWaiting && <Waiting />}

      <ChatInput
        loading={isSending}
        disabled={isSending || isWaiting}
        onMessageSend={handleSend}
        passedFiles={passedFiles}
      />
    </div>
  );
};

export default Chat;
